import { clamp, cloneDeep, isNumber } from 'lodash';
import IconFont from '@meitu/xdesign-icon-font';
import { getFramework, ResourceThumbnail } from '@/framework';
import { getLocaleStore } from '@/i18n/index';
import { base64Kit } from '@/utils/base64-kit';
import { deleteExtraInfo } from '@/utils/layer-kit';
import {
    BrushType,
    LayerType,
    type IActiveBaseInfo,
    type IBrushLayerProps,
    type IExportBehavior,
    type ILayerBehavior,
    type ILayerOriginInfo,
    type ILayerProps,
    type ISize,
} from '@/version-types';
import { normalizeBrushLayerDasharray } from './config';
import { EdgelessBrushLayer } from './edgeless-layer';
import { canPreviewBrushInteraction } from './render/interaction-preview';
import { renderLayerContentToCanvas } from './render/render-to-canvas';
import { renderLayerToPPT } from './render/render-to-ppt';
import { renderLayerToStage } from './render/render-to-stage';
import {
    BRUSH_POINT_SIZE,
    generateLinePath,
    getDefaultBrushAttribute,
    getDefaultBrushPoint,
    getPathOffset,
    isCodeSvg,
    LAYER_EDGE_GAP,
} from './utils';
import { getScreenshotUrlCode, initializeBrushLayerScreenshotUrl, uploadBrushFile } from './utils/screenshot';
import ActiveOperate from './views/active-operate';

function getBehavior(identity: string) {
    const layerBehavior: ILayerBehavior = {
        getEdgelessObject(layer: ILayerProps) {
            return new EdgelessBrushLayer(layer.id, getFramework(identity));
        },

        onBeforeAddOrUpdate(layer) {
            const data = layer.data as IBrushLayerProps;
            // 补全被客户端丢失的默认值
            if (!data.stroke) {
                data.stroke = getDefaultBrushAttribute();
            }
            if (!data.startPoint) {
                data.startPoint = getDefaultBrushPoint();
            }
            if (!data.startPoint.offset) {
                data.startPoint.offset = getDefaultBrushPoint().offset;
            }
            if (!data.endPoint) {
                data.endPoint = getDefaultBrushPoint();
            }
            if (!data.endPoint.offset) {
                data.endPoint.offset = getDefaultBrushPoint().offset;
            }
            normalizeBrushLayerDasharray(layer);
            initializeBrushLayerScreenshotUrl(layer);
        },

        async onBeforeSaveAsync(layer: ILayerProps, isAutoSave?: boolean) {
            const data = layer.data as IBrushLayerProps;
            deleteExtraInfo(layer);
            const { width, height } = layer;

            // 检查曲线资源是否存在
            if (data.brushType === BrushType.Curve && !data.url && data.__drawPath) {
                const res = await uploadBrushFile(data.__drawPath, data.naturalWidth, data.naturalHeight);
                if (res.url) {
                    data.url = res.url;
                }
            }

            if (width > 0 && height > 0) {
                // 上传缩略图
                const code = await getScreenshotUrlCode(identity, layer);

                // 获取远程缩略图地址
                data.screenshotUrl = await base64Kit.getRemoteUrlAsync(code);
            } else {
                data.screenshotUrl = '';
            }

            // console.log(layer.data);
        },

        async getResultCanvasAsync(layer: ILayerProps, resultSize: ISize): Promise<HTMLCanvasElement | undefined> {
            return renderLayerToStage(layer, identity, resultSize);
        },

        async getLayerImageAsync(layer: ILayerProps, globalAlpha = true, needScaleCanvas = false) {
            // 缩略图仅用于列表预览，不能为九千像素的笔迹再次创建同尺寸 Canvas。
            const scale = needScaleCanvas ? Math.min(1, 512 / Math.max(layer.width, layer.height, 1)) : 1;
            return renderLayerContentToCanvas(cloneDeep(layer), identity, globalAlpha, scale);
        },

        async savePPTLayerAsync(slide, layer) {
            return renderLayerToPPT(slide, layer, identity);
        },

        getActiveBaseInfo(layer: ILayerProps): IActiveBaseInfo {
            /**
             * 箭头、直线 在直线操作里限制
             * 这里只限制通用的操作
             */
            const data = layer.data as IBrushLayerProps;
            const isCode = isCodeSvg(data.brushType);
            if (isCode) {
                return {
                    cutWidth: Math.max(1, data.stroke.width * 2),
                    cutHeight: Math.max(1, data.stroke.width * 2),
                    radius: data.radius || 0,
                    stroke: cloneDeep(data.stroke),
                };
            }

            if (data.brushType === BrushType.Line) {
                return {
                    cutWidth: data.stroke.width * 2 + LAYER_EDGE_GAP + 1,
                    cutHeight: data.stroke.width * 2 + LAYER_EDGE_GAP + 1,
                    stroke: cloneDeep(data.stroke),
                };
            }

            if (data.brushType === BrushType.Arrow) {
                const startPoint = data.startPoint;
                const endPoint = data.endPoint;

                return {
                    cutWidth: Math.ceil(
                        Math.max(startPoint.width / BRUSH_POINT_SIZE || 1, endPoint.width / BRUSH_POINT_SIZE || 1),
                    ),
                    cutHeight: Math.ceil(
                        Math.max(startPoint.height / BRUSH_POINT_SIZE || 1, endPoint.height / BRUSH_POINT_SIZE || 1),
                    ),
                };
            }

            if (data.brushType === BrushType.Wavy) {
                return {
                    cutWidth: data.stroke.width * 2,
                    cutHeight: data.naturalHeight,
                };
            }

            const res = getPathOffset(data.fills, data.stroke);
            return {
                cutWidth: res.offsetWidth + 1,
                cutHeight: res.offsetHeight + 1,
                fills: cloneDeep(data.fills),
                stroke: cloneDeep(data.stroke),
            };
        },
        getScaleMinRatio(originInfo: ILayerOriginInfo, layer: ILayerProps): number {
            const { width, height, cutHeight, cutWidth } = originInfo;

            const data = layer.data as IBrushLayerProps;
            if (
                data.brushType === BrushType.Line ||
                data.brushType === BrushType.Arrow ||
                data.brushType === BrushType.Wavy
            ) {
                const offHeight = height - cutHeight;
                return (height - offHeight) / height;
            }

            const offWidth = width - cutWidth;
            return (width - offWidth) / width;
        },
        getScaleIsNeedLimitSize(): boolean {
            return true;
        },
        onBeforeScaleUpdate(layer: ILayerProps, { originInfo }) {
            const data = layer.data as IBrushLayerProps;
            const originWidth = originInfo?.width || 0;
            const ratio = layer.width / originWidth;

            const stroke = originInfo?.stroke;
            if (stroke) {
                data.stroke.width = Math.ceil(stroke.width * ratio);
                data.stroke.offsetX = Math.ceil(stroke.offsetX * ratio);
                data.stroke.offsetY = Math.ceil(stroke.offsetY * ratio);
            }

            if (data.brushType === BrushType.Line) {
                const width = clamp(layer.height - LAYER_EDGE_GAP * 2 - data.stroke.width * 2, 1, layer.height);
                data.fills[0].width = width;
                const fillWidth = layer.width - LAYER_EDGE_GAP * 2 - data.stroke.width * 2;
                data.__drawPath = generateLinePath({ x: 0, y: 0 }, { x: fillWidth, y: 0 });
                data.naturalWidth = fillWidth;
            }
            if (data.brushType === BrushType.Arrow) {
                const startPoint = data.startPoint;
                const endPoint = data.endPoint;
                const startWidth = ~~((BRUSH_POINT_SIZE / startPoint.height) * layer.height);
                const endWidth = ~~((BRUSH_POINT_SIZE / endPoint.height) * layer.height);
                data.fills[0].width = Math.max(startWidth, endWidth, 1);
            }
            if (data.brushType === BrushType.Wavy) {
                console.log(originInfo);
            }
            if (data.brushType === BrushType.Rect) {
                if (isNumber(data.radius) && isNumber(originInfo?.radius)) {
                    data.radius = Math.floor(originInfo.radius * ratio);
                }
            }
            if (data.brushType === BrushType.Curve) {
                const fills = originInfo?.fills;
                if (fills?.length) {
                    fills.forEach((item, index) => {
                        data.fills[index].width = Math.ceil(item.width * ratio);
                        data.fills[index].offsetX = Math.ceil(item.offsetX * ratio);
                        data.fills[index].offsetY = Math.ceil(item.offsetY * ratio);
                    });
                }
            }
        },

        getMenuOverlapName(layer: ILayerProps) {
            const { brushType } = layer.data as IBrushLayerProps;
            const { $t } = getLocaleStore();
            if (brushType === BrushType.Line || brushType === BrushType.Arrow) {
                return $t((v) => v.brushLayer.Behavior.line);
            }
            if (brushType === BrushType.Curve || brushType === BrushType.Wavy) {
                return $t((v) => v.brushLayer.Behavior.curve);
            }
            return $t((v) => v.brushLayer.Behavior.shape);
        },

        getMenuOverlapIcon(layer: ILayerProps) {
            if (layer.width <= 0 || layer.height <= 0) {
                return <IconFont name="BrushAndBoard" fontSize={16} />;
            }

            const resource = getFramework(identity).service.edgelessCanvas.getByLayerId(layer.id)?.resource;
            let { screenshotUrl } = layer.data as IBrushLayerProps;
            if (screenshotUrl) {
                screenshotUrl = base64Kit.getRemoteUrl(screenshotUrl);
            }
            return (
                <ResourceThumbnail
                    resource={resource}
                    fallbackIcon={<IconFont name="BrushAndBoard" fontSize={16} />}
                    fallbackUrl={screenshotUrl}
                />
            );
        },

        customActiveOperate(layer) {
            const brushType = (layer.data as IBrushLayerProps).brushType;
            if (brushType === BrushType.Line || brushType === BrushType.Arrow || brushType === BrushType.Wavy) {
                return <ActiveOperate layer={layer} />;
            }

            return null;
        },

        disableDefaultScaleOperate(layer) {
            if ((layer.data as IBrushLayerProps).brushType === BrushType.Curve) return false;
            return true;
        },

        disableDefaultResizeOperate(layer) {
            if ((layer.data as IBrushLayerProps).brushType === BrushType.Curve) return false;
            return true;
        },

        // 笔刷属性仍由原操作链路实时计算；仅跳过拖动过程中的整层资源栅格化。
        scaleOnlyUpdateBaseInfo: canPreviewBrushInteraction,
        resizeOnlyUpdateBaseInfo: canPreviewBrushInteraction,
    };

    return layerBehavior;
}

const exportBehavior: IExportBehavior = {
    layerType: LayerType.BRUSH_LAYER,
    getBehavior,
};

export default exportBehavior;
